import React, { useEffect, useRef, useState } from "react";
import { Box, Skeleton, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import {
  CandlestickChartOutlined,
  NightsStay as NightsStayIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useCompanyQuote } from "./Charts/hooks/useCompanyQuote";
import { useExchangeMarketHours } from "./Charts/hooks/useExchangeMarketHours";
import { impliedIndexLevel, isCmeTradingNow } from "./impliedIndexLevel";
import { MARKET_INDICES, MarketIndex } from "./config/marketIndices";
import MiniChart from "./Charts/MiniChart";
import OverviewEconomicCalendar from "./OverviewEconomicCalendar";
import {
  flashText,
  panelBorder,
  panelHairline,
  panelSurface,
  panelTitleSx,
  riseIn,
  sectionHeaderSx,
} from "./overviewPanel";

dayjs.extend(utc);
dayjs.extend(timezone);

// The dashboard home's top band: the market at a glance on the left, the
// economic calendar on the right, each a floating header over open cards —
// the same headerless idiom as the watchlist and news below, so the whole
// page reads as one system. The two columns still end on the same line: the
// calendar fills the height the markets grid sets and scrolls past it.
//
// Each index card stacks name, level-and-move and the day's shape, and a
// tick flashes the level by direction (green up, red down) through the DOM
// so the sparkline never re-renders.

// The home's market glance, in this exact order.
const BAND_INDEX_TICKERS = ["^GSPC", "^IXIC", "^DJI", "^FTMC", "CLUSD", "^VIX"];
const BAND_INDICES = BAND_INDEX_TICKERS.map((ticker) =>
  MARKET_INDICES.find((index) => index.ticker === ticker)
).filter((index): index is MarketIndex => index !== undefined);

// The cell's floor and the grid's gap, shared with the page skeleton so the
// real band lands exactly where its placeholder stood: name line, level
// line and the chart's 42px minimum, plus padding.
export const BAND_CELL_HEIGHT = 108;
export const BAND_CELL_GAP = 10;

// A clock the band re-reads once a minute, so the states that are pure
// clock arithmetic (the US after-hours window, the CME session) flip on
// their own instead of waiting for a feed change to re-render them. Paused
// while the tab is hidden; coming back catches it up.
const useNow = (intervalMs: number): Date => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => {
      if (!document.hidden) setNow(new Date());
    };
    const id = window.setInterval(tick, intervalMs);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [intervalMs]);
  return now;
};

// Big indices read as whole numbers; Oil and VIX need their decimals.
const formatLevel = (price: number | null | undefined): string => {
  if (price == null || !Number.isFinite(price)) return "—";
  if (price >= 1000) return price.toLocaleString("en-US", { maximumFractionDigits: 0 });
  return price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatMove = (pct: number | null | undefined): string => {
  if (pct == null || !Number.isFinite(pct)) return "—";
  return `${pct > 0 ? "+" : ""}${pct.toFixed(2)}%`;
};

interface IndexCellProps {
  index: MarketIndex;
  /** Whether the CME session is live. Outside it the future's last print is
   *  stale and is never shown as the index's level. */
  cmeOpen: boolean;
}

const IndexCell: React.FC<IndexCellProps> = ({ index, cmeOpen }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { data: quoteData, isLoading } = useCompanyQuote(index.ticker);
  const quote = Array.isArray(quoteData) ? quoteData[0] : quoteData;

  // Out-of-hours coverage, same rule as the Sectors page's grid: while the
  // US market is closed, cells with a mapped CME e-mini future show the
  // future's live move translated onto the index's own scale (previous
  // close × the future's %), and chart the future's ~24h session — but only
  // while the CME session is live, and only when the future's move squares
  // with the index's last price (impliedIndexLevel's guard); otherwise the
  // cell stays on the index's own quote.
  const { data: usMarketHours } = useExchangeMarketHours(index.futuresTicker ? "NYSE" : undefined);
  const showFutures =
    !!index.futuresTicker && usMarketHours?.isMarketOpen === false && cmeOpen;
  const { data: futuresQuoteData } = useCompanyQuote(showFutures ? index.futuresTicker! : "");
  const futuresQuote = Array.isArray(futuresQuoteData) ? futuresQuoteData[0] : futuresQuoteData;
  const futuresPct = futuresQuote?.changePercentage;
  const impliedLevel = showFutures
    ? impliedIndexLevel(quote?.previousClose, futuresPct, quote?.price)
    : null;
  const usingFutures = impliedLevel != null;

  const displayPrice = usingFutures ? impliedLevel : quote?.price;
  const displayPct = usingFutures ? futuresPct : quote?.changePercentage;
  const chartTicker = usingFutures ? index.futuresTicker! : index.ticker;
  const chartFiltersHours = usingFutures ? false : index.category !== "commodity";

  const priceRef = useRef<HTMLElement>(null);
  const pctRef = useRef<HTMLElement>(null);
  const prevPriceRef = useRef<number | undefined>(undefined);
  const neutral = theme.palette.text.secondary;

  useEffect(() => {
    if (displayPrice == null || !Number.isFinite(displayPrice)) return;
    const prev = prevPriceRef.current;
    prevPriceRef.current = displayPrice;
    // Only a real move flashes — a steady value stays still.
    if (prev === undefined || displayPrice === prev) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    flashText(priceRef.current, displayPrice > prev ? "#22c55e" : "#ef4444");
    flashText(pctRef.current, neutral);
  }, [displayPrice, neutral]);

  const changeColor =
    displayPct == null || displayPct === 0
      ? theme.palette.text.secondary
      : displayPct > 0
        ? "#22c55e"
        : "#ef4444";

  return (
    <Box
      sx={{
        backgroundColor: panelSurface(isDark),
        border: `1px solid ${panelBorder(isDark)}`,
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
        minWidth: 0,
        minHeight: BAND_CELL_HEIGHT,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: theme.palette.text.secondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {index.name}
        </Typography>
        {/* Says so when the level is the future's, not the index's — and
            puts the future's actual print one hover (or long-press) away */}
        {usingFutures && (
          <Tooltip
            title={`Implied from the ${index.futuresTicker} future, last ${formatLevel(
              futuresQuote?.price
            )} (${formatMove(futuresPct)})`}
            enterTouchDelay={300}
          >
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: theme.palette.text.secondary,
                border: `1px solid ${panelHairline(isDark)}`,
                borderRadius: 0.5,
                px: 0.375,
                flexShrink: 0,
                cursor: "default",
              }}
            >
              FUT
            </Typography>
          </Tooltip>
        )}
      </Box>

      {isLoading && (
        <Skeleton
          variant="rounded"
          animation="wave"
          height={20}
          sx={{
            width: "70%",
            borderRadius: 0.5,
            bgcolor: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.09)",
          }}
        />
      )}
      {!isLoading && (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.875, minWidth: 0 }}>
          <Typography
            ref={priceRef}
            sx={{
              // Phones get a notch smaller so level and move always share
              // the cell's line without clipping
              fontSize: { xs: "0.9375rem", sm: "1.0625rem" },
              fontWeight: 700,
              color: theme.palette.text.primary,
              lineHeight: 1.15,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {formatLevel(displayPrice)}
          </Typography>
          <Typography
            ref={pctRef}
            sx={{
              fontSize: { xs: "0.6875rem", sm: "0.75rem" },
              fontWeight: 600,
              color: changeColor,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {displayPct == null && "—"}
            {displayPct != null && displayPct > 0 && "+"}
            {displayPct != null && `${displayPct.toFixed(2)}%`}
          </Typography>
        </Box>
      )}

      {/* The day's shape is the card's floor and its flex slack: when the
          calendar beside the grid stretches the band, the extra height goes
          to the chart rather than to empty padding. */}
      <Box sx={{ minWidth: 0, flex: 1, minHeight: 42, mt: 0.25, position: "relative" }}>
        <MiniChart
          ticker={chartTicker}
          width="responsive"
          height="responsive"
          strokeWidth={3}
          changePercentage={displayPct}
          filterTradingHours={chartFiltersHours}
          sessionWindow={usingFutures ? undefined : index.session}
          refetchIntervalMs={60 * 1000}
        />
      </Box>
    </Box>
  );
};

// Which market is actually trading right now. London opens first, New York
// takes over at 14:30 UK time, and the US extended session carries the tape
// after the closing bell — the state every level in the panel depends on.
//
// FMP reports each exchange's open/closed state (holidays included); it does
// NOT report the extended session, so the after-hours window is computed on
// the New York clock, re-read every minute so the pill flips to Closed at
// 20:00 ET on its own. A US market holiday afternoon is the one case that
// reads optimistically: the feed carries no "opened today" flag to gate on.
const isUsAfterHours = (now: Date): boolean => {
  const newYork = dayjs(now).tz("America/New_York");
  const weekday = newYork.day();
  if (weekday === 0 || weekday === 6) return false;
  const minutes = newYork.hour() * 60 + newYork.minute();
  return minutes >= 16 * 60 && minutes < 20 * 60;
};

type MarketState = {
  label: string;
  color: string;
  /** Live tape: the marker breathes. A closed market sits still. */
  live: boolean;
  afterHours: boolean;
};

const resolveMarketState = (
  theme: Theme,
  usOpen: boolean,
  ukOpen: boolean,
  now: Date
): MarketState => {
  if (usOpen) return { label: "US Open", color: "#22c55e", live: true, afterHours: false };
  if (ukOpen) return { label: "UK Open", color: "#22c55e", live: true, afterHours: false };
  if (isUsAfterHours(now))
    return { label: "After Hours", color: "#3b82f6", live: true, afterHours: true };
  return { label: "Closed", color: theme.palette.text.secondary, live: false, afterHours: false };
};

interface MarketStatusProps {
  /** The band's minute clock: the after-hours window is clock arithmetic
   *  and has to flip between feed updates. */
  now: Date;
}

const MarketStatus: React.FC<MarketStatusProps> = ({ now }) => {
  const theme = useTheme();
  const { data: usHours } = useExchangeMarketHours("NYSE");
  const { data: ukHours } = useExchangeMarketHours("LSE");
  // Nothing to claim until at least one exchange has answered
  if (!usHours && !ukHours) return null;

  const state = resolveMarketState(
    theme,
    usHours?.isMarketOpen === true,
    ukHours?.isMarketOpen === true,
    now
  );

  const pulseSx = {
    animation: "marketStatePulse 2.4s ease-in-out infinite",
    "@keyframes marketStatePulse": {
      "0%": { opacity: 1 },
      "50%": { opacity: 0.35 },
      "100%": { opacity: 1 },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  } as const;

  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
      {/* Hairline tick separating the state from the panel's name */}
      <Box
        sx={{
          width: "1px",
          height: 12,
          backgroundColor: panelHairline(isDark),
          mr: 0.375,
          flexShrink: 0,
        }}
      />
      {/* The overnight tape gets the moon the stock cards already use for
          after-hours prices; a regular session gets the dot. */}
      {state.afterHours && (
        <NightsStayIcon
          sx={{ fontSize: 13, color: state.color, flexShrink: 0, ...pulseSx }}
        />
      )}
      {!state.afterHours && (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: state.color,
            flexShrink: 0,
            ...(state.live && pulseSx),
          }}
        />
      )}
      <Typography
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: state.color,
          whiteSpace: "nowrap",
        }}
      >
        {state.label}
      </Typography>
    </Box>
  );
};

const OverviewMarketBand: React.FC = () => {
  const theme = useTheme();
  const now = useNow(60 * 1000);
  const cmeOpen = isCmeTradingNow(now);

  return (
    <Box
      sx={{
        display: "grid",
        // The markets matrix leads; the calendar holds a narrower column
        // beside it. Below lg they stack, calendar second.
        gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.9fr) minmax(0, 1fr)" },
        gap: { xs: 2, md: 2.5 },
        alignItems: "stretch",
        mb: { xs: 4, md: 5 },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, ...riseIn(0) }}>
        {/* Floating header, same idiom as the watchlist and news below:
            green mark, uppercase title — and the session state WITH the
            title, since it qualifies what the cards are showing. */}
        <Box sx={sectionHeaderSx}>
          <CandlestickChartOutlined sx={{ fontSize: 15, color: "#22c55e", flexShrink: 0 }} />
          <Typography
            sx={{
              ...panelTitleSx,
              color: theme.palette.text.secondary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Markets
          </Typography>
          <MarketStatus now={now} />
        </Box>
        {/* Six cards divide evenly at both column counts, so the grid is
            never left with an orphan. */}
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
            gridAutoRows: "minmax(0, 1fr)",
            gap: `${BAND_CELL_GAP}px`,
          }}
        >
          {BAND_INDICES.map((index) => (
            <IndexCell key={index.ticker} index={index} cmeOpen={cmeOpen} />
          ))}
        </Box>
      </Box>

      <OverviewEconomicCalendar enterDelayMs={70} />
    </Box>
  );
};

export default OverviewMarketBand;
